import type { JSX } from 'react';
import { BarChart3, Headphones, Wrench, Zap } from 'lucide-react';
import { Card } from '@components/ui/Card';
import { Container } from '@components/ui/Container';
import { RevealOnView } from '@components/ui/RevealOnView';
import { SectionHeading } from '@components/ui/SectionHeading';
import { landingCopy } from '@features/landing/data/landing.pt';
import type { ServicoCard } from '@features/landing/data/types';

const ICON_BY_KEY: Record<ServicoCard['iconKey'], JSX.Element> = {
  sistemas: <Wrench className="h-6 w-6" aria-hidden="true" />,
  automacoes: <Zap className="h-6 w-6" aria-hidden="true" />,
  paineis: <BarChart3 className="h-6 w-6" aria-hidden="true" />,
  suporte: <Headphones className="h-6 w-6" aria-hidden="true" />,
};

const STAGGER_DELAY_MS = 100;

/**
 * Secao "Servicos / O que fazemos" (SECT-04 — PRD §4.5).
 *
 * Grid de 4 cards (UI-02 `Card`) — cada card e um servico que a Omanna
 * entrega: Sistemas sob medida, Automacoes, Paineis, Suporte. Layout:
 *  - Mobile: 1 col stack
 *  - Tablet (sm+): 2 cols
 *  - Desktop (lg+): 4 cols inline
 *
 * Background: `bg-white` (alternancia com Problema `bg-neutral-50`).
 * Anchor: `#servicos` (acessado pelo menu Servicos do Navbar).
 *
 * Reveal: header + 4 cards com stagger CSS-only de 100ms.
 */
export const Servicos = (): JSX.Element => {
  const { servicos } = landingCopy;

  return (
    <section id="servicos" aria-labelledby="servicos-heading" className="bg-white py-20 lg:py-28">
      <Container>
        <RevealOnView>
          <SectionHeading
            as="h2"
            eyebrow={servicos.eyebrow}
            title={servicos.title}
            align="left"
            className="max-w-3xl"
            titleId="servicos-heading"
          />
        </RevealOnView>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {servicos.cards.map((card, index) => (
            <RevealOnView key={card.iconKey} delayMs={STAGGER_DELAY_MS * (index + 1)}>
              <Card
                icon={ICON_BY_KEY[card.iconKey]}
                title={card.title}
                description={card.description}
              />
            </RevealOnView>
          ))}
        </div>
      </Container>
    </section>
  );
};
